import * as Yup from "yup";
import { isPostalCodeValid } from "../../addresses/helpers.js";
import type { OnboardingFormVariant } from "./onboardingUpdateInput.js";
import {
  websiteUrlPrefillValidationSchema,
  websiteUrlValidationSchema,
} from "./websiteUrl.js";

/** The field names the schema's built-in messages mention, and the custom messages. */
export interface OnboardingFormSchemaMessages {
  street: string;
  postalCode: string;
  city: string;
  country: string;
  email: string;
  invalidPostalCode: string;
  confirmAddress: string;
  /** The hint of a required field left empty. */
  required: string;
}

/**
 * Validation of the form step. The business step has no required field: every
 * check applies only to a value that was entered. The property owner's contact
 * email is the one required value.
 *
 * The address is optional as a whole: with street, postcode and city all blank
 * (the preset country alone does not count) it is valid and is not saved.
 * Once any of the three is entered, all three are required, the postcode is
 * checked against the format of the selected country and, while
 * `requireConfirmedAddress` is set (the Google Places service is available),
 * the address must carry a location, i.e. come from a picked suggestion, the
 * website lookup or the stored address. `initialWebsite` is the stored
 * website: it stays passable while unchanged (legacy values of any scheme), an
 * edited website must also pass the lookup box's http(s) check.
 */
export function createOnboardingFormSchema({
  variant,
  requireConfirmedAddress,
  initialWebsite,
  messages,
}: {
  variant: OnboardingFormVariant;
  requireConfirmedAddress: boolean;
  initialWebsite: string | null;
  messages: OnboardingFormSchemaMessages;
}) {
  const address = Yup.object()
    .shape({
      streetLines1: Yup.string().label(messages.street),
      postalCode: Yup.string().label(messages.postalCode),
      city: Yup.string().label(messages.city),
      countryCode: Yup.string().label(messages.country),
      location: Yup.object()
        .shape({ lat: Yup.number(), lng: Yup.number() })
        .nullable()
        .default(undefined),
    })
    .test("address-checks", function (value) {
      if (!value) {
        return true;
      }
      const isBlank = (text: string | undefined) => !text || !text.trim();
      if (
        isBlank(value.streetLines1) &&
        isBlank(value.postalCode) &&
        isBlank(value.city)
      ) {
        return true;
      }
      const errors: Yup.ValidationError[] = [];
      const required = (part: string) =>
        errors.push(
          this.createError({
            path: `${this.path}.${part}`,
            message: messages.required,
          })
        );
      if (isBlank(value.streetLines1)) {
        required("streetLines1");
      }
      if (isBlank(value.postalCode)) {
        required("postalCode");
      }
      if (isBlank(value.city)) {
        required("city");
      }
      if (isBlank(value.countryCode)) {
        required("countryCode");
      }
      if (
        !isBlank(value.postalCode) &&
        !isPostalCodeValid({
          postalCode: value.postalCode ?? "",
          countryCode: value.countryCode ?? "",
        })
      ) {
        errors.push(
          this.createError({
            path: `${this.path}.postalCode`,
            message: messages.invalidPostalCode,
          })
        );
      }
      if (
        requireConfirmedAddress &&
        !isBlank(value.streetLines1) &&
        !value.location
      ) {
        errors.push(
          this.createError({
            path: `${this.path}.streetLines1`,
            message: messages.confirmAddress,
          })
        );
      }
      return errors.length > 0 ? new Yup.ValidationError(errors) : true;
    });

  const email = Yup.string().label(messages.email).email().nullable();

  return Yup.object().shape({
    website: websiteUrlValidationSchema().test(
      "edited-website-is-strict",
      "Please enter a valid website URL",
      value =>
        !value ||
        value === initialWebsite ||
        websiteUrlPrefillValidationSchema().isValidSync(value)
    ),
    propertyName: Yup.string(),
    address,
    email: variant === "property" ? email.required(messages.required) : email,
    phones: Yup.array().of(
      Yup.object().shape({
        main: Yup.boolean().required(),
        type: Yup.string().required(),
        number: Yup.string(),
      })
    ),
  });
}
